'use strict';

/**
 * The helpers exported in this file are used to calculate
 * the creator details that will appear on the pdp.
 * Copy for the details titles is not determined in these helpers.
 * These methods return only the information that will
 * appear under a detail's title.
 */

const _ = require('underscore');
const sharedHelpers = require('./detailsSharedLogic');
const findNameSpace = require('find-namespace-value');

// Private Methods

/**
 *
 * Get an array of objects that DetailsItem.jsx will use to render this detail. Each object will have a key
 * 'value', which holds the display name of a creator.
 *
 * @param {object} config
 * @param {object} config.item                              - Bunsen Item Model
 * @param {string} config.linksKey                          - the key under which links for this detail can be found in config.links
 * @param {object} config.links                             - see this.detailLinks in ItemStore for definition {@link newPdp/ItemStore}
 * @param {object} config.creatorRoleReferences             - see this.creatorRoleReferences in ItemStore for definition {@link newPdp/ItemStore}
 * @param {object} config.attributions                      - the attributions you want creators for
 * @param {boolean} config.attributions.[attribution name]  - the boolean represents if you want a the creator who has that attribution
 *
 * @returns {{
 *               value: string,
 *               isLink: boolean,
 *               link: string,
 *               extraCopy: string
 *          }[]}
 *
 * ie: [{
 *          value: 'Tomaso Buzzi',
 *          isLink: true,
 *          link: '/creators/tomaso-buzzi/furniture/',
 *          extraCopy: '(Designer)'
 *     }]
 */
function getCreatorsByAttribution(config) {
    const {item, linksKey, links, creatorRoleReferences, attributions} = config;
    const contents = [];
    const creators = item.get('creators');

    if (!creators) {
        return contents;
    }

    creators.each((creator) => {
        const content = {isLink: false};
        const attribution = creator.get('attribution');
        const roles = creator.get('roles');
        let link;

        // Display name for creator
        // Default to creator.name if creator.displayName is unavailable
        content.value = creator.get('creator.displayName') || creator.get('creator.name');

        if (!_.isEmpty(roles)) {
            // If creator has a role, we want to show that next to the creator's
            // displayName on the pdp. So we have to save that information for the
            // component that will display it.
            const creatorRole = roles[0];
            const creatorRoleDisplayName = findNameSpace(`${creatorRole}.displayName`, creatorRoleReferences);

            if (creatorRoleDisplayName) {
                content.extraCopy = `(${creatorRoleDisplayName})`;
            }
        }

        // Check if there is a link for the type of detail
        // ie links && links['creator']
        if (links && links[linksKey]) {
            const value = content.value;
            // Grab the link for that type of detail and the creator's display name
            // ie links['creator'] = {'Tomaso Buzzi': '/creators/tomaso-buzzi/furniture/'} so
            // link = links['creator']['Tomaso Buzzi']
            link = links[linksKey][value];
        }

        if (link) {
            content.isLink = true;
            content.link = link;
        }

        // We want the object for this creator only if it has an attribution
        // matching the attributions marked as true in config.attributions object
        if (content.value && attribution && attributions[attribution]) {
            contents.push(content);
        }
    });

    return contents;
}

/**
 * Get the dates that the creator was born and/or died based
 * on what information is available on the creator model.
 *
 * @param {object} creator  - Bunsen Creator model
 * @returns {string}        - DOB-DOD|b. DOB|d. DOD
 */
function getCreatorsArtDate(creator) {
    const dateOfBirth = creator.get('creator.dateOfBirth');
    const dateOfDeath = creator.get('creator.dateOfDeath');
    const fallbackDate = creator.get('creator.date');

    const birthDateText = `${dateOfDeath ? '' : 'b. '}${dateOfBirth}`;
    const deathDateText = `${dateOfBirth ? '-' : 'd. '}${dateOfDeath}`;
    const formattedDate = `${dateOfBirth ? birthDateText : ''}${dateOfDeath ? deathDateText : ''}`;

    return formattedDate || fallbackDate;
}

/**
 *
 * Get a display name for art creators
 *
 * @param {object} nameData
 * @param {string} nameData.name               - artist display name
 * @param {string} [nameData.date]             - dates artist was born and/or died (optional)
 * @param {string} [nameData.countryOfOrigin]  - artist country of origin (optional)
 * @returns {string}                           - Artist Name, (DOB-DOD|b. DOB|d. DOD, Country)
 *
 * ie: Timothy Roepe (1962, United States)
 */
function formatCreatorsArtName(nameData) {
    const {name, date, countryOfOrigin} = nameData;
    let extraInfo = [date, countryOfOrigin];
    let value;

    extraInfo = extraInfo.filter((info) => {
        return !!info;
    });

    if (name) {
        const extraInfoString = extraInfo.length > 0 ? ` (${extraInfo.join(', ')})` : '';
        value = `${name}${extraInfoString}`;
    }

    return value;
}

function getCreatorName(creator = []) {
    if (creator.length && typeof creator[0].get === 'function') {
        return creator[0].get('creator.name');
    }
    return '';
}

function getFashionJewelryCreator({ item, links }) {
    return sharedHelpers.addToContents({
        passedValue: getCreatorName(item.getCreatorsByRole('DESIGNER')),
        links,
        linkKey: 'creator'
    });
}

function getCreatorByType({ config, type = '' }) {
    const { item } = config;
    let creatorName = '';
    if (item && typeof item.getCreatorsByRole === 'function') {
        creatorName = getCreatorName(item.getCreatorsByRole(type.toUpperCase()));
    }
    return sharedHelpers.getSingleValueWithoutLink(config, type, creatorName);
}

module.exports = {
    /**
     * See definition for private method getCreatorsByAttribution above
     */
    getCreatorsFurniture: function (config) {
        const inflatedConfig = Object.assign({
            attributions: {BY: true, BY_AND_DOCUMENTED: true},
            linksKey: 'creator'
        }, config);
        return getCreatorsByAttribution(inflatedConfig);
    },

    /**
     * See definition for addToContents in detailsSharedLogic {@link newPdp/details/helpers/detailsSharedLogic}
     */
    getCreatorJewelry: config => getFashionJewelryCreator(config),

    /**
     * See definition for addToContents in DetailsSharedLogic {@link newPdp/details/helpers/detailsSharedLogic}
     */
    getCreatorArt: function (config) {
        const {item, links} = config;
        const creators = item.get('creators');
        const creator = creators && creators.first();
        let value;

        if (creator) {
            // Default to creator.name if creator.displayName is unavailable
            const name = creator.get('creator.displayName') || creator.get('creator.name');
            const date = getCreatorsArtDate(creator);
            const countryOfOrigin = creator.get('creator.countryOfOrigin');

            value = formatCreatorsArtName({name, date, countryOfOrigin});
        }

        return sharedHelpers.addToContents({
            passedValue: value,
            links,
            linkKey: 'creator'
        });
    },

    /**
     * See definition for addToContents in detailsSharedLogic {@link newPdp/details/helpers/detailsSharedLogic}
     */
    getCreatorFashion: config => getFashionJewelryCreator(config),

    /**
     * See definition for getSingleValueWithoutLink in detailsSharedLogic {@link newPdp/details/helpers/detailsSharedLogic}
     */
    getManufacturer: config => getCreatorByType({ config, type: 'manufacturer' }),

    /**
     * See definition for getSingleValueWithoutLink in detailsSharedLogic {@link newPdp/details/helpers/detailsSharedLogic}
     */
    getDesigner: config => getCreatorByType({ config, type: 'designer' })
};



// WEBPACK FOOTER //
// ./assets/js/newPdp/details/helpers/detailsCreators.es.js